import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Envelope, ChatCircle, DeviceMobile } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { fmtYmdTime } from "@/lib/dateFormat";

export default function Mailbox() {
    const { user } = useAuth();
    const isAdmin = ["super_admin", "company_admin"].includes(user?.role);
    const [tab, setTab] = useState(isAdmin ? "company" : "inbox");
    const [inbox, setInbox] = useState([]);
    const [company, setCompany] = useState([]);
    const [line, setLine] = useState([]);
    const [sms, setSms] = useState([]);
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        api.get("/mailbox/inbox").then((r) => setInbox(r.data));
        if (isAdmin) {
            api.get("/mailbox/company").then((r) => setCompany(r.data)).catch(() => {});
            api.get("/mailbox/line-logs").then((r) => setLine(r.data)).catch(() => {});
            api.get("/mailbox/sms-logs").then((r) => setSms(r.data)).catch(() => {});
        }
    }, [isAdmin]);

    const currentList = tab === "inbox" ? inbox : tab === "company" ? company : tab === "line" ? line : sms;

    const previewEmail = async (template_type) => {
        try {
            const { data } = await api.get("/notifications/email-preview", { params: { template_type } });
            const w = window.open("", "_blank", "width=720,height=900");
            if (w) {
                w.document.write(data.html);
                w.document.close();
            }
        } catch (e) {
            alert("プレビューの取得に失敗しました");
        }
    };

    return (
        <div className="space-y-5" data-testid="mailbox-page">
            <div className="flex justify-between items-start flex-wrap gap-3">
                <div>
                    <p className="prc-label-overline">社内メール・ログセンター</p>
                    <h1 className="text-[1.685rem] font-bold tracking-tight">サーバー内蔵メールサーバー</h1>
                    <p className="text-sm text-[var(--prc-muted)] mt-1">通知系（社内メール・LINE・SMS）のログを一元確認できます。</p>
                </div>
                {isAdmin && (
                    <div className="flex gap-2" data-testid="email-preview-buttons">
                        <button
                            onClick={() => previewEmail("inspection_reminder")}
                            className="prc-btn-ghost text-xs"
                            data-testid="preview-inspection-reminder"
                        >
                            点検リマインド プレビュー
                        </button>
                        <button
                            onClick={() => previewEmail("missed_inspection")}
                            className="prc-btn-ghost text-xs"
                            data-testid="preview-missed-inspection"
                        >
                            点検漏れ プレビュー
                        </button>
                        <button
                            onClick={() => previewEmail("invoice_issued")}
                            className="prc-btn-ghost text-xs"
                            data-testid="preview-invoice-issued"
                        >
                            請求書 プレビュー
                        </button>
                    </div>
                )}
            </div>

            <div className="flex gap-1 border-b border-[var(--prc-border)]" data-testid="mailbox-tabs">
                <Tab id="inbox" cur={tab} setTab={setTab} icon={<Envelope size={16} />} label={`受信箱 (${inbox.length})`} />
                {isAdmin && <Tab id="company" cur={tab} setTab={setTab} icon={<Envelope size={16} />} label={`社内メール全体 (${company.length})`} />}
                {isAdmin && <Tab id="line" cur={tab} setTab={setTab} icon={<ChatCircle size={16} />} label={`LINE ログ (${line.length})`} />}
                {isAdmin && <Tab id="sms" cur={tab} setTab={setTab} icon={<DeviceMobile size={16} />} label={`SMS ログ (${sms.length})`} />}
            </div>

            <div className="grid lg:grid-cols-5 gap-4">
                <div className="prc-card lg:col-span-2 overflow-hidden">
                    <ul className="divide-y divide-[var(--prc-border)] max-h-[600px] overflow-auto" data-testid={`mail-list-${tab}`}>
                        {currentList.length === 0 ? <li className="p-4 text-sm text-[var(--prc-muted)]">データがありません</li> :
                            currentList.map((m) => (
                                <li key={m.id}>
                                    <button onClick={() => setSelected(m)} className={`w-full text-left p-4 hover:bg-[var(--prc-bg)] ${selected?.id === m.id ? "bg-[var(--prc-bg)]" : ""}`} data-testid={`mail-item-${m.id}`}>
                                        <p className="font-semibold text-sm truncate">{m.subject || m.title || (tab === "sms" ? (m.to_phone || "SMS") : "LINE通知")}</p>
                                        <p className="text-xs text-[var(--prc-muted)] mt-0.5 truncate">
                                            {tab === "inbox" || tab === "company" ? `From: ${m.from_email} → ${m.to_email}` :
                                                tab === "line" ? `To user: ${m.to_user_id}` : `To: ${m.to_phone || "-"}`}
                                        </p>
                                        <p className="text-xs text-[var(--prc-tertiary)] mt-1">{fmtYmdTime(m.created_at, String(m.created_at).substring(0, 16).replace("T", " "))} · {m.status}</p>
                                    </button>
                                </li>
                            ))}
                    </ul>
                </div>
                <div className="prc-card lg:col-span-3 p-5">
                    {!selected ? <p className="text-sm text-[var(--prc-muted)]" data-testid="mail-empty">左の一覧から選択してください</p> :
                        <div data-testid="mail-detail">
                            <div className="flex items-center gap-2">
                                <p className="prc-label-overline">詳細</p>
                                {selected.template_type && <span className="prc-badge info text-[10px]">{selected.template_type}</span>}
                                {selected.branding_source === "tenant" && <span className="prc-badge ok text-[10px]">WHITE LABEL</span>}
                            </div>
                            <h2 className="text-lg font-bold mt-1">{selected.subject || selected.title || (selected.to_phone || "")}</h2>
                            <p className="text-xs text-[var(--prc-muted)] mt-1">{String(selected.created_at).substring(0, 19).replace("T", " ")} · status: {selected.status}</p>
                            {selected.html ? (
                                <iframe srcDoc={selected.html} title="email-html" className="mt-4 w-full h-[520px] border border-[var(--prc-border)] rounded-sm bg-white" data-testid="mail-html-frame" />
                            ) : (
                                <div className="mt-4 whitespace-pre-wrap text-sm">{selected.body}</div>
                            )}
                            {selected.note && <p className="mt-4 text-xs text-[var(--prc-muted)] p-3 bg-[var(--prc-bg)] border border-[var(--prc-border)] rounded-sm">{selected.note}</p>}
                        </div>
                    }
                </div>
            </div>
        </div>
    );
}

function Tab({ id, cur, setTab, icon, label }) {
    return (
        <button onClick={() => setTab(id)} className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold border-b-2 transition ${cur === id ? "border-[var(--prc-ink)] text-[var(--prc-ink)]" : "border-transparent text-[var(--prc-muted)]"}`} data-testid={`mail-tab-${id}`}>
            {icon}{label}
        </button>
    );
}
